import type { ExtendedColumn } from '@/pages/team/datasets/[datasetId]/schema/utils';
import ArrowDownOutlined from '@ant-design/icons/ArrowDownOutlined';
import ArrowUpOutlined from '@ant-design/icons/ArrowUpOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Button from 'antd/es/button';
import Checkbox from 'antd/es/checkbox';
import Collapse from 'antd/es/collapse';
import Divider from 'antd/es/divider';
import Empty from 'antd/es/empty';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import InputNumber from 'antd/es/input-number';
import message from 'antd/es/message';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Switch from 'antd/es/switch';
import Tabs from 'antd/es/tabs';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useState } from 'react';
import { styled, useTheme } from 'styled-components';

// ---------- Types ------------------------------------------------------------
import type { Operation } from '@mai/types';

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

// Helper type guards
const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean';
const isString = (value: unknown): value is string => typeof value === 'string';
const isNumber = (value: unknown): value is number => typeof value === 'number';
const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

interface ColumnEditorProps {
  column: ExtendedColumn;
  onUpdate: (column: ExtendedColumn) => void;
  isEditable: boolean;
}

// ---------- Styled -----------------------------------------------------------
const EditorContainer = styled.div`
  ${({ theme }) => `
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.paddingXL}px;
`}
`;

const EditorHeader = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginXL}px;
`}
`;

const EditorContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const OperationItem = styled.div`
  ${({ theme }) => `
  .ant-collapse-header {
    padding: ${theme.paddingSM}px ${theme.padding}px !important;
  }
  
  .operation-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  
  .operation-title {
    display: flex;
    align-items: center;
    gap: ${theme.sizeXS}px;
  }
  
  .operation-actions {
    display: flex;
    align-items: center;
    gap: ${theme.sizeXXS}px;
  }
`}
`;

const FormSection = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginXL}px;
`}
`;

const HelpText = styled(Text)`
  ${({ theme }) => `
  display: block;
  margin-top: ${theme.marginXXS}px;
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
`}
`;

// ---------- Operation Templates ----------------------------------------------
const OPERATION_TEMPLATES: Record<string, Operation> = {
  strip_whitespace: {
    type: 'strip_whitespace',
    parameters: {
      chars: null,
      sides: 'both',
    },
  },
  convert_dtype: {
    type: 'convert_dtype',
    parameters: {
      target_type: 'string',
      handle_floats: true,
      coerce_errors: false,
    },
  },
  validate_range: {
    type: 'validate_range',
    parameters: {
      min_value: null,
      max_value: null,
      inclusive: true,
    },
  },
  extract_pattern: {
    type: 'extract_pattern',
    parameters: {
      patterns: [''] as string[],
      case_sensitive: false,
      return_matches: true,
      clean_trailing: '',
      extract_type: 'matches',
    },
  },
};

const OPERATION_LABELS: Record<string, string> = {
  strip_whitespace: 'Strip Whitespace',
  convert_dtype: 'Convert Data Type',
  validate_range: 'Validate Range',
  extract_pattern: 'Extract Pattern',
};

// ---------- Main Component ---------------------------------------------------
const ColumnEditor: React.FC<ColumnEditorProps> = ({
  column,
  onUpdate,
  isEditable,
}) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('properties');
  const [expandedOperations, setExpandedOperations] = useState<string[]>([]);
  const theme = useTheme();

  const handleFieldChange = (
    field: keyof ExtendedColumn,
    value: ExtendedColumn[keyof ExtendedColumn],
  ) => {
    if (!isEditable) return;
    onUpdate({ ...column, [field]: value });
  };

  const handleAddOperation = (type: string) => {
    if (!isEditable) return;

    const template = OPERATION_TEMPLATES[type];
    if (!template) return;

    const operations = column.operations || [];
    const newOperation = { ...template };

    onUpdate({
      ...column,
      operations: [...operations, newOperation],
    });

    // Expand the new operation
    setExpandedOperations([...expandedOperations, `${operations.length}`]);
    void message.success('Operation added');
  };

  const handleUpdateOperation = (index: number, operation: Operation) => {
    if (!isEditable) return;

    const operations = [...(column.operations || [])];
    operations[index] = operation;

    onUpdate({
      ...column,
      operations,
    });
  };

  const handleDeleteOperation = (index: number) => {
    if (!isEditable) return;

    const operations = [...(column.operations || [])];
    operations.splice(index, 1);

    onUpdate({
      ...column,
      operations,
    });

    void message.success('Operation removed');
  };

  const handleMoveOperation = (index: number, direction: 'up' | 'down') => {
    if (!isEditable) return;

    const operations = [...(column.operations || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= operations.length) return;

    [operations[index], operations[newIndex]] = [
      operations[newIndex],
      operations[index],
    ];

    onUpdate({
      ...column,
      operations,
    });
  };

  const getAvailableOperations = () => {
    const allOperations = Object.keys(OPERATION_TEMPLATES);

    // Filter operations based on column type
    const typeSpecificOperations: Record<string, string[]> = {
      string: ['strip_whitespace', 'extract_pattern', 'convert_dtype'],
      integer: ['convert_dtype', 'validate_range'],
      float: ['convert_dtype', 'validate_range'],
      boolean: ['convert_dtype'],
      datetime: ['convert_dtype', 'validate_range'],
      json: ['convert_dtype'],
      array: ['convert_dtype'],
    };

    return (typeSpecificOperations[column.type] || allOperations).map((op) => ({
      value: op,
      label: OPERATION_LABELS[op] || op,
    }));
  };

  const renderOperationParameters = (operation: Operation, index: number) => {
    switch (operation.type) {
      case 'strip_whitespace':
        return (
          <>
            <Form.Item label="Sides">
              <Select
                value={
                  isString(operation.parameters?.sides)
                    ? operation.parameters.sides
                    : 'both'
                }
                onChange={(value) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: { ...operation.parameters, sides: value },
                  });
                }}
                disabled={!isEditable}
              >
                <Option value="both">Both</Option>
                <Option value="left">Left</Option>
                <Option value="right">Right</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Characters"
              extra="Leave empty to strip all whitespace"
            >
              <Input
                value={
                  isString(operation.parameters?.chars)
                    ? operation.parameters.chars
                    : ''
                }
                onChange={(e) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: {
                      ...operation.parameters,
                      chars: e.target.value || null,
                    },
                  });
                }}
                disabled={!isEditable}
                placeholder="e.g., .-_"
              />
            </Form.Item>
          </>
        );

      case 'convert_dtype':
        return (
          <>
            <Form.Item label="Target Type">
              <Select
                value={
                  isString(operation.parameters?.target_type)
                    ? operation.parameters.target_type
                    : 'string'
                }
                onChange={(value) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: { ...operation.parameters, target_type: value },
                  });
                }}
                disabled={!isEditable}
              >
                <Option value="string">String</Option>
                <Option value="integer">Integer</Option>
                <Option value="float">Float</Option>
                <Option value="boolean">Boolean</Option>
                <Option value="datetime">DateTime</Option>
              </Select>
            </Form.Item>

            {isString(operation.parameters?.target_type) &&
              operation.parameters.target_type === 'integer' && (
                <Form.Item>
                  <Checkbox
                    checked={
                      isBoolean(operation.parameters?.handle_floats)
                        ? operation.parameters.handle_floats
                        : false
                    }
                    onChange={(e) => {
                      handleUpdateOperation(index, {
                        ...operation,
                        parameters: {
                          ...operation.parameters,
                          handle_floats: e.target.checked,
                        },
                      });
                    }}
                    disabled={!isEditable}
                  >
                    Handle floats (truncate decimal part)
                  </Checkbox>
                </Form.Item>
              )}

            <Form.Item>
              <Checkbox
                checked={
                  isBoolean(operation.parameters?.coerce_errors)
                    ? operation.parameters.coerce_errors
                    : false
                }
                onChange={(e) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: {
                      ...operation.parameters,
                      coerce_errors: e.target.checked,
                    },
                  });
                }}
                disabled={!isEditable}
              >
                Convert errors to NULL
              </Checkbox>
            </Form.Item>

            {isString(operation.parameters?.target_type) &&
              operation.parameters.target_type === 'datetime' && (
                <Form.Item
                  label="Date Format"
                  extra="e.g., %Y-%m-%d for 2023-01-01"
                >
                  <Input
                    value={
                      isString(operation.parameters?.date_format)
                        ? operation.parameters.date_format
                        : ''
                    }
                    onChange={(e) => {
                      handleUpdateOperation(index, {
                        ...operation,
                        parameters: {
                          ...operation.parameters,
                          date_format: e.target.value,
                        },
                      });
                    }}
                    disabled={!isEditable}
                    placeholder="%Y-%m-%d"
                  />
                </Form.Item>
              )}
          </>
        );

      case 'validate_range':
        return (
          <>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Range"
                style={{ marginBottom: 0, width: '50%' }}
              >
                <InputNumber
                  value={
                    isNumber(operation.parameters?.min_value)
                      ? operation.parameters.min_value
                      : undefined
                  }
                  onChange={(value) => {
                    handleUpdateOperation(index, {
                      ...operation,
                      parameters: { ...operation.parameters, min_value: value },
                    });
                  }}
                  disabled={!isEditable}
                  placeholder="Min"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, width: '50%' }}>
                <InputNumber
                  value={
                    isNumber(operation.parameters?.max_value)
                      ? operation.parameters.max_value
                      : undefined
                  }
                  onChange={(value) => {
                    handleUpdateOperation(index, {
                      ...operation,
                      parameters: { ...operation.parameters, max_value: value },
                    });
                  }}
                  disabled={!isEditable}
                  placeholder="Max"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Space.Compact>

            <Form.Item>
              <Checkbox
                checked={
                  isBoolean(operation.parameters?.inclusive)
                    ? operation.parameters.inclusive
                    : true
                }
                onChange={(e) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: {
                      ...operation.parameters,
                      inclusive: e.target.checked,
                    },
                  });
                }}
                disabled={!isEditable}
              >
                Inclusive range (include min/max values)
              </Checkbox>
            </Form.Item>
          </>
        );

      case 'extract_pattern':
        return (
          <>
            <Form.Item label="Patterns">
              {(isStringArray(operation.parameters?.patterns)
                ? operation.parameters.patterns
                : []
              ).map((pattern: string, patternIndex: number) => (
                <Space
                  key={patternIndex}
                  style={{
                    display: 'flex',
                    marginBottom: `${theme.marginXS}px`,
                  }}
                >
                  <Input
                    value={pattern}
                    onChange={(e) => {
                      const patterns = isStringArray(
                        operation.parameters?.patterns,
                      )
                        ? [...operation.parameters.patterns]
                        : [''];
                      patterns[patternIndex] = e.target.value;
                      handleUpdateOperation(index, {
                        ...operation,
                        parameters: { ...operation.parameters, patterns },
                      });
                    }}
                    disabled={!isEditable}
                    placeholder="Regular expression"
                    style={{ width: `${theme.controlHeightLG / 0.1}px` }}
                  />
                  {isEditable &&
                    isStringArray(operation.parameters?.patterns) &&
                    operation.parameters.patterns.length > 1 && (
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => {
                          const patterns = isStringArray(
                            operation.parameters?.patterns,
                          )
                            ? [...operation.parameters.patterns]
                            : [];
                          patterns.splice(patternIndex, 1);
                          handleUpdateOperation(index, {
                            ...operation,
                            parameters: { ...operation.parameters, patterns },
                          });
                        }}
                      />
                    )}
                </Space>
              ))}
              {isEditable && (
                <Button
                  type="dashed"
                  onClick={() => {
                    const patterns = isStringArray(
                      operation.parameters?.patterns,
                    )
                      ? [...operation.parameters.patterns, '']
                      : [''];
                    handleUpdateOperation(index, {
                      ...operation,
                      parameters: { ...operation.parameters, patterns },
                    });
                  }}
                  style={{ width: '100%' }}
                >
                  Add Pattern
                </Button>
              )}
            </Form.Item>

            <Form.Item label="Extract Type">
              <Select
                value={
                  isString(operation.parameters?.extract_type)
                    ? operation.parameters.extract_type
                    : 'matches'
                }
                onChange={(value) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: {
                      ...operation.parameters,
                      extract_type: value,
                    },
                  });
                }}
                disabled={!isEditable}
              >
                <Option value="matches">Matches</Option>
                <Option value="groups">Groups</Option>
                <Option value="named_groups">Named Groups</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Checkbox
                checked={
                  isBoolean(operation.parameters?.case_sensitive)
                    ? operation.parameters.case_sensitive
                    : false
                }
                onChange={(e) => {
                  handleUpdateOperation(index, {
                    ...operation,
                    parameters: {
                      ...operation.parameters,
                      case_sensitive: e.target.checked,
                    },
                  });
                }}
                disabled={!isEditable}
              >
                Case sensitive
              </Checkbox>
            </Form.Item>
          </>
        );

      default:
        return <Text type="secondary">No configuration available</Text>;
    }
  };

  const renderOperationHeader = (operation: Operation, index: number) => {
    const canMoveUp = index > 0;
    const canMoveDown = index < (column.operations || []).length - 1;

    return (
      <div className="operation-header">
        <div className="operation-title">
          <Tag color="blue">Step {index + 1}</Tag>
          <Text strong>
            {OPERATION_LABELS[operation.type] || operation.type}
          </Text>
        </div>
        {isEditable && (
          <div
            className="operation-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="Move up">
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={!canMoveUp}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMoveOperation(index, 'up');
                }}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={!canMoveDown}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMoveOperation(index, 'down');
                }}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteOperation(index);
                }}
              />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <EditorContainer>
      <EditorHeader>
        <Title level={4} style={{ margin: 0 }}>
          {column.name}
        </Title>
        <Text type="secondary">Configure properties and operations</Text>
      </EditorHeader>

      <EditorContent>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Properties" key="properties">
            <Form layout="vertical" form={form}>
              <FormSection>
                <Form.Item label="Column Name" required>
                  <Input
                    value={column.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    disabled={!isEditable}
                    placeholder="column_name"
                  />
                  <HelpText>Use lowercase with underscores, no spaces</HelpText>
                </Form.Item>

                <Form.Item label="Source Column">
                  <Input
                    value={column.source}
                    onChange={(e) =>
                      handleFieldChange('source', e.target.value)
                    }
                    disabled={!isEditable}
                    placeholder="Original column name or index"
                  />
                  <HelpText>
                    Name or index of the column in source data
                  </HelpText>
                </Form.Item>

                <Space size="large" style={{ width: '100%' }}>
                  <Form.Item label="Data Type" style={{ flex: 1 }}>
                    <Select
                      value={column.type}
                      onChange={(value) => handleFieldChange('type', value)}
                      disabled={!isEditable}
                      style={{ width: '100%' }}
                    >
                      <Option value="string">String / Text</Option>
                      <Option value="integer">Integer</Option>
                      <Option value="float">Float</Option>
                      <Option value="boolean">Boolean</Option>
                      <Option value="datetime">DateTime</Option>
                      <Option value="json">JSON</Option>
                      <Option value="array">Array</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item label="Allow NULL">
                    <Switch
                      checked={column.nullable}
                      onChange={(checked) =>
                        handleFieldChange('nullable', checked)
                      }
                      disabled={!isEditable}
                    />
                  </Form.Item>
                </Space>

                <Form.Item label="Description">
                  <TextArea
                    value={column.description || ''}
                    onChange={(e) =>
                      handleFieldChange('description', e.target.value)
                    }
                    disabled={!isEditable}
                    placeholder="Describe the purpose of this column"
                    rows={3}
                  />
                </Form.Item>

                {column.transformExpr !== undefined && (
                  <Form.Item
                    label={
                      <Space>
                        Transform Expression
                        <Tooltip title="Advanced SQL or expression to transform data">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                  >
                    <Input
                      value={column.transformExpr || ''}
                      onChange={(e) =>
                        handleFieldChange('transformExpr', e.target.value)
                      }
                      disabled={!isEditable}
                      placeholder="e.g., UPPER(column_name)"
                    />
                  </Form.Item>
                )}
              </FormSection>
            </Form>
          </TabPane>

          <TabPane
            tab={`Operations (${(column.operations || []).length})`}
            key="operations"
          >
            {(column.operations || []).length === 0 ? (
              <Empty
                description="No operations configured"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Collapse
                activeKey={expandedOperations}
                onChange={setExpandedOperations}
                destroyInactivePanel
              >
                {(column.operations || []).map((operation: Operation, index: number) => (
                  <Panel
                    key={`${index}`}
                    header={renderOperationHeader(operation, index)}
                  >
                    <OperationItem>
                      <Form layout="vertical">
                        {renderOperationParameters(operation, index)}
                      </Form>
                    </OperationItem>
                  </Panel>
                ))}
              </Collapse>
            )}

            {isEditable && (
              <>
                <Divider />
                <Select
                  placeholder="Add an operation"
                  style={{ width: '100%' }}
                  onChange={handleAddOperation}
                  value={undefined}
                >
                  {getAvailableOperations().map((op: { value: string; label: string }) => (
                    <Option key={op.value} value={op.value}>
                      <Space>
                        <PlusOutlined />
                        {op.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </>
            )}
          </TabPane>
        </Tabs>
      </EditorContent>
    </EditorContainer>
  );
};

export default ColumnEditor;
